<template>
  <v-menu
    offset-y
    left
    :close-on-content-click="false"
    max-width="360"
    content-class="permission-app"
  >
    <template #activator="{ on, attrs }">
      <button
        type="button"
        class="flex items-center justify-center w-8 h-8 rounded-md text-slate-500 hover:bg-slate-100 transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-indigo-300"
        :aria-label="$t('permission_help_aria')"
        aria-haspopup="dialog"
        v-bind="attrs"
        v-on="on"
      >
        <v-icon size="16">mdi-help-circle-outline</v-icon>
      </button>
    </template>

    <div class="permission-help-popover bg-white rounded-lg p-4 text-xs text-slate-700 shadow-lg border border-slate-200">
      <section class="mb-3">
        <div class="font-semibold text-slate-800 mb-1">
          {{ $t('permission_matrix_help_title') }}
        </div>
        <div class="text-slate-600 leading-relaxed">
          {{ hintText }}
        </div>
        <div v-if="showUnionRule" class="text-slate-600 leading-relaxed mt-1">
          {{ $t('permission_union_rule_line', { role: roleLabel }) }}
        </div>
      </section>

      <section class="mb-3">
        <div class="font-semibold text-slate-800 mb-1.5">
          {{ $t('permission_legend_label') }}
        </div>
        <div class="legend-grid">
          <span class="legend-item">
            <span class="dot dot-indigo" aria-hidden="true"></span>
            {{ $t('permission_legend_inherited') }}
          </span>
          <span class="legend-item">
            <span class="dot dot-emerald" aria-hidden="true"></span>
            {{ $t('permission_legend_grant') }}
          </span>
          <span class="legend-item">
            <span class="dot dot-rose" aria-hidden="true"></span>
            {{ $t('permission_legend_revoke') }}
          </span>
          <span class="legend-item">
            <span class="dot dot-slate" aria-hidden="true"></span>
            {{ $t('permission_legend_none') }}
          </span>
        </div>
      </section>

      <section class="mb-3">
        <div class="font-semibold text-slate-800 mb-1.5">
          {{ $t('permission_kind_legend_label') }}
        </div>
        <div class="kind-list">
          <div v-for="k in KIND_KEYS" :key="k" class="kind-item">
            <v-icon size="12" :color="kindIconColor(k)">{{ KIND_BADGE[k].icon }}</v-icon>
            <span class="font-medium">{{ $t(KIND_BADGE[k].labelKey) }}</span>
            <span class="text-slate-500">· {{ $t('permission_kind_' + k + '_hint') }}</span>
          </div>
        </div>
      </section>

      <section>
        <div class="font-semibold text-slate-800 mb-1">
          {{ $t('permission_link_rule_panel_title') }}
        </div>
        <div class="text-slate-600">• {{ $t('permission_link_rule_grant_line') }}</div>
        <div class="text-slate-600">• {{ $t('permission_link_rule_revoke_line') }}</div>
      </section>
    </div>
  </v-menu>
</template>

<script>
import { KIND_BADGE, kindIconColor } from '@/js/permission-helpers'

const KIND_KEYS = ['menu', 'page', 'feature']

export default {
  name: 'PermissionHelpPopover',
  props: {
    // 'user' | 'role'：決定頂部 hint 文案
    mode: {
      type: String,
      default: 'user',
      validator: v => ['user', 'role'].includes(v)
    },
    // roles 模式需帶入「部門 / 群組」短標籤以填 union rule
    roleLabel: { type: String, default: '' }
  },
  data () {
    return { KIND_BADGE, KIND_KEYS }
  },
  computed: {
    hintText () {
      return this.mode === 'role'
        ? this.$t('permission_role_matrix_hint')
        : this.$t('permission_user_matrix_hint')
    },
    showUnionRule () {
      // 只有 role 模式顯示聯集規則（多部門 / 多群組的 OR 合併）
      return this.mode === 'role' && !!this.roleLabel
    }
  },
  methods: {
    kindIconColor
  }
}
</script>

<style>
/* Popover 被 teleport 到 body，scoped style 失效 — 用 global style（class prefix 避污染） */
.permission-help-popover { max-width: 360px; }
.permission-help-popover .legend-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4px 8px;
}
.permission-help-popover .legend-item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.permission-help-popover .dot {
  display: inline-block;
  width: 6px;
  height: 6px;
  border-radius: 9999px;
  flex: 0 0 auto;
}
.permission-help-popover .dot-emerald { background: #10b981; }
.permission-help-popover .dot-indigo { background: #6366f1; }
.permission-help-popover .dot-rose { background: #f43f5e; }
.permission-help-popover .dot-slate { background: #cbd5e1; }
.permission-help-popover .kind-list > .kind-item {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 2px 0;
}
</style>
